import { useEffect, MutableRefObject } from 'react';
import { usePrevious, usePersistFn } from 'zoom/hooks';
import { isShallowEqual } from 'utils/util';
import { CellLayout } from 'shared/types/video.interface';
import { MediaStream, Participants } from 'index-types';

export function useRenderVideo(
  mediaStream: MediaStream | null,
  isVideoDecodeReady: boolean,
  videoRef: MutableRefObject<HTMLCanvasElement | null>,
  subscribedVideos: number[],
  participantsLayout: {
    user: Participants;
    layout: CellLayout;
  }[],
  currentUserId?: number,
) {
  const previousSubscribedVideos = usePrevious(subscribedVideos);
  const previousParticipantsLayout = usePrevious(participantsLayout);
  const previousIsVideoDecodeReady = usePrevious(isVideoDecodeReady);

  useEffect(() => {
    if (
      videoRef.current &&
      participantsLayout &&
      participantsLayout.length > 0 &&
      isVideoDecodeReady
    ) {
      const addedSubscribers = subscribedVideos.filter(
        (id) => !(previousSubscribedVideos || []).includes(id),
      );

      const removedSubscribers = (previousSubscribedVideos || []).filter(
        (id: number) => !subscribedVideos.includes(id),
      );

      const unalteredSubscribers = subscribedVideos.filter((id) =>
        (previousSubscribedVideos || []).includes(id),
      );

      if (removedSubscribers.length > 0) {
        removedSubscribers.forEach(async (userId: number) => {
          await mediaStream?.stopRenderVideo(videoRef.current as HTMLCanvasElement, userId);
          // }
        });
      }
      if (addedSubscribers.length > 0) {
        // In the addedSubscribers.forEach, replace the existing logging with:
        addedSubscribers.forEach(async (userId) => {
          const found = participantsLayout.find((l) => l.user.userId === userId);
          if (!found) return;
          const { layout: cellDimension } = found;
          if (cellDimension) {
            const { width, height, x, y, quality } = cellDimension;
            await mediaStream?.renderVideo(
              videoRef.current as HTMLCanvasElement,
              userId,
              width,
              height,
              x,
              y,
              quality,
            );
          }
        });
      }
      if (unalteredSubscribers.length > 0) {
        // layout changed
        if (
          previousParticipantsLayout &&
          participantsLayout.length > 0 &&
          previousParticipantsLayout.length > 0 &&
          (participantsLayout.length !== previousParticipantsLayout.length ||
            !isShallowEqual(participantsLayout[0]?.layout, previousParticipantsLayout[0]?.layout))
        ) {
          unalteredSubscribers.forEach((userId) => {
            const index = participantsLayout.findIndex((l) => l.user.userId === userId);
            if (index === -1) return;
            const cellDimension = participantsLayout[index]?.layout;
            if (cellDimension) {
              const { width, height, x, y, quality } = cellDimension;
              if (
                previousParticipantsLayout &&
                previousParticipantsLayout[index] &&
                previousParticipantsLayout[index]?.layout?.quality !== quality
              ) {
                mediaStream?.renderVideo(
                  videoRef.current as HTMLCanvasElement,
                  userId,
                  width,
                  height,
                  x,
                  y,
                  quality,
                );
              }
              mediaStream?.adjustRenderedVideoPosition(
                videoRef.current as HTMLCanvasElement,
                userId,
                width,
                height,
                x,
                y,
              );
            }
          });
        }
        // the order of participants changed
        const participantsIds = participantsLayout.map((el) => el.user.userId);
        const previousParticipantsIds = previousParticipantsLayout?.map((el) => el.user.userId);
        if (participantsIds.join('-') !== previousParticipantsIds?.join('-')) {
          unalteredSubscribers.forEach((userId) => {
            const index = participantsIds.findIndex((id) => id === userId);
            const previousIndex = previousParticipantsIds?.findIndex((id) => id === userId);
            if (index !== previousIndex && index !== -1) {
              const cellDimension = participantsLayout[index]?.layout;
              window.console.log(
                cellDimension,
                'cellDimension in unalteredSubscribers participantsIds !== previousParticipantsIds',
              );
              if (cellDimension) {
                const { width, height, x, y } = cellDimension;
                mediaStream?.adjustRenderedVideoPosition(
                  videoRef.current as HTMLCanvasElement,
                  userId,
                  width,
                  height,
                  x,
                  y,
                );
              }
            }
          });
        }
      }
    }
  }, [
    mediaStream,
    isVideoDecodeReady,
    videoRef,
    participantsLayout,
    previousParticipantsLayout,
    subscribedVideos,
    previousSubscribedVideos,
    currentUserId,
  ]);

  useEffect(() => {
    if (
      previousIsVideoDecodeReady === false &&
      isVideoDecodeReady === true &&
      subscribedVideos.length > 0
    ) {
      subscribedVideos.forEach(async (userId) => {
        const found = participantsLayout.find((l) => l.user.userId === userId);
        if (!found) return;
        const { layout: cellDimension } = found;

        if (cellDimension) {
          const { width, height, x, y, quality } = cellDimension;
          window.console.log(videoRef.current, 'rendering subscribedVideos');
          await mediaStream?.renderVideo(
            videoRef.current as HTMLCanvasElement,
            userId,
            width,
            height,
            x,
            y,
            quality,
          );
        }
      });
    }
  }, [
    mediaStream,
    videoRef,
    participantsLayout,
    subscribedVideos,
    isVideoDecodeReady,
    previousIsVideoDecodeReady,
    currentUserId,
  ]);
  const stopAllVideos = usePersistFn((videoCanvasDOM: HTMLCanvasElement) => {
    if (subscribedVideos.length > 0) {
      subscribedVideos.forEach((userId) => {
        mediaStream?.stopRenderVideo(videoCanvasDOM, userId);
      });
    }
  });

  useEffect(() => {
    const videoCanvasDOM = videoRef.current;
    return () => {
      if (videoCanvasDOM) stopAllVideos(videoCanvasDOM);
    };
  }, [videoRef, stopAllVideos]);
}
